import { Box, Button, Link as MuiLink } from '@mui/material';
import { IssuePlanVsFactReportResult } from '@tap/data-providers';
import { useDocumentSchemaContext } from '@tap/doc-ui';
import { getGeneratedDynamicLink, IComponentCss, numberHelper, XGridTableWrap } from '@tap/mui-ui';
import { StatusChip } from '@tap/tap-ui';
import { StatusStyleCode, StatusChipVariant } from '@tap/tap-ui/src/StatusChip/status.types';
import { Link } from 'react-router';
import {
    GridCellParams as IMuiGridCellParams,
} from '@mui/x-data-grid-pro';
import clsx from 'clsx';
import { RoutesData } from '../../../routes/routes';

/* 
    Таблица План-факт
*/

// Цвета для ячеек со значениями
const css: IComponentCss = {
    tableWrap: {
        position: 'relative',
        paddingTop: '40px',
        '& .red-value': {
            color: '#cf4343',
        },
        '& .black-value': {
            color: 'black',
        },
    },
    optionsButton: {
        zIndex: 1,
        position: 'absolute',
        right: '4px',
        top: '5px',
        height: '28px',
    },
};

const getCellClassName = (spentTime: number, estimateTime: number): string => {
    return (clsx('table-wrap', {
        'red-value': (spentTime > estimateTime),
        'black-value': (spentTime < estimateTime),
    }))
}

type ComparisonOfStatisticsTableProps = {
    reportData: IssuePlanVsFactReportResult[],
    onOptionsButtonClick: () => void,
}

export default function ComparisonOfStatisticsTable({ reportData, onOptionsButtonClick }: ComparisonOfStatisticsTableProps) {
    const { schemaList } = useDocumentSchemaContext();
    const rowsList = reportData.map((item, idx) => ({ ...item, id: idx }))

    return (
        <Box sx={css.tableWrap}>
            <Button
                size='small'
                sx={css.optionsButton}
                onClick={onOptionsButtonClick}
            >
                Параметры отчёта
            </Button>
            <XGridTableWrap
                rows={rowsList}
                dataIsLoading={false}
                tableHeight={'calc(100vh - 160px)'}
                columns={[
                    {
                        field: 'documentKey',
                        flex: 0.6,
                        sortable: false,
                        headerName: 'Заявка',
                        type: 'string',
                        renderCell: (cellParams) => (
                            <MuiLink
                                component={Link}
                                to={getGeneratedDynamicLink(
                                    RoutesData.detailsPageRoutes.path,
                                    [{ key: 'documentKey', value: cellParams.row.documentKey }],
                                )}
                            >
                                {cellParams.row.documentKey}
                            </MuiLink>
                        ),
                    },
                    {
                        field: 'title',
                        headerName: 'Название',
                        sortable: false,
                        type: 'string',
                        flex: 1,
                    },
                    {
                        field: 'status',
                        headerName: 'Статус',
                        sortable: false,
                        type: 'string',
                        flex: 0.6,
                        renderCell: cellParams => {
                            const schema = schemaList.find(s => s.documentCode === cellParams.row.documentKey.split('-')[0]);
                            const statusList = schema?.settings.statusList || [];
                            const statusProps = statusList.find(p => p.code === cellParams.row.status) || {
                                code: cellParams.row.status,
                                name: cellParams.row.status,
                                styleCode: StatusStyleCode.Red,
                            };
                            return (
                                <StatusChip
                                    styleCode={statusProps.styleCode as StatusStyleCode}
                                    statusName={statusProps.name}
                                    variant={StatusChipVariant.Contained}
                                />
                            )
                        },
                    },
                    {
                        field: 'estimateTimeMin',
                        headerName: 'Плановое время',
                        sortable: false,
                        type: 'number',
                        width: 150,
                        valueGetter: (_value, row) => numberHelper.formatNumberWithDotDelimiter(row.estimateTimeMin / 60, 2),
                        renderCell: (params) => `${numberHelper.formatNumberWithDotDelimiter(params.row.estimateTimeMin / 60, 2)} ч`,
                    },
                    {
                        field: 'spentTimeMin',
                        headerName: 'Затраченное время',
                        sortable: false,
                        type: 'number',
                        width: 150,
                        valueGetter: (_value, row) => numberHelper.formatNumberWithDotDelimiter(row.spentTimeMin / 60, 2),
                        renderCell: (params) => `${numberHelper.formatNumberWithDotDelimiter(params.row.spentTimeMin / 60, 2)} ч`,
                    },
                    {
                        field: 'difference',
                        headerName: 'Отклонение',
                        sortable: false,
                        type: 'number',
                        width: 150,
                        renderCell: (params) => {
                            const difference = numberHelper.formatNumberWithDotDelimiter(((params.row.spentTimeMin / 60) - (params.row.estimateTimeMin / 60)), 2);
                            return `${difference > 0 ? '+' : ''}${difference} ч`
                        },
                        valueGetter: (_value, row) => numberHelper.formatNumberWithDotDelimiter(((row.spentTimeMin / 60) - (row.estimateTimeMin / 60)), 2),
                        cellClassName: (params: IMuiGridCellParams<number>) =>
                            getCellClassName(params.row.spentTimeMin / 60, params.row.estimateTimeMin / 60),
                    },
                ]}

            />
        </Box>
    );
}

